import { useEffect, ReactElement, PropsWithChildren } from 'react';
import { DFPSlotsProvider, DFPManager } from 'react-dfp';
import { useLocation } from 'react-router-dom';

import useAuth from 'common/hooks/useAuth';
import { useAdManagerQuery, Grade } from 'common/apollo/graphql';
import { env } from 'utils/env';

type AdManagerProps = PropsWithChildren<{}>;

const AD_NETWORK_ID = env.AD_NETWORK_ID;
const GRADE_MAP = {
  [Grade.None]: undefined,
  [Grade.PreKindergarten]: '0',
  [Grade.Kindergarten]: '0',
  [Grade.First]: '1',
  [Grade.Second]: '2',
  [Grade.Third]: '3',
  [Grade.Fourth]: '4',
  [Grade.Fifth]: '5',
  [Grade.Sixth]: '6',
  [Grade.Seventh]: '7',
  [Grade.Eighth]: '8',
  [Grade.Ninth]: '9',
  [Grade.Tenth]: '10',
  [Grade.Eleventh]: '11',
  [Grade.Twelfth]: '12',
  [Grade.HigherEducation]: '13',
  [Grade.Adult]: '13',
};
const getUnique = (array?: string[]): string[] => Array.from(new Set(array));
const isDefined = <T,>(argument: T | undefined): argument is T =>
  argument !== undefined;

const AdManager = ({ children }: AdManagerProps): ReactElement => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { data } = useAdManagerQuery({
    skip: !isAuthenticated,
  });

  const hideAd = data?.me?.isAdFreeSubscribed;

  useEffect(() => {
    if (data) {
      const schoolInfo = data.me?.schoolInfo;
      const schoolInfoForChildren = data.me?.schoolInfoForChildren ?? [];
      const role = data.me?.role;
      const grades = getUnique(
        schoolInfoForChildren
          .map((school) => school?.grade)
          .concat(schoolInfo?.grade)
          .map((grade) => grade && GRADE_MAP[grade])
          .filter(isDefined)
      );
      DFPManager.setTargetingArguments({
        role,
        grade: grades,
      });

      if (!hideAd) {
        DFPManager.load();
      }
    }
  }, [data, hideAd]);

  useEffect(() => {
    DFPManager.refresh();
  }, [location]);

  return (
    <DFPSlotsProvider dfpNetworkId={AD_NETWORK_ID} autoLoad={false}>
      {children}
    </DFPSlotsProvider>
  );
};

export default AdManager;
